import React, { useState } from 'react';
import {
  Box,
  Container,
  Input,
  InputGroup,
  InputLeftElement,
  VStack,
  Text,
  useColorModeValue,
  Spinner,
  Alert,
  AlertIcon,
  Grid,
  GridItem,
  Stack,
  Heading,
  Link,
  useToast,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  HStack,
  Badge,
  Wrap,
  WrapItem,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  List,
  ListItem,
  ListIcon,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
} from '@chakra-ui/react';
import { SearchIcon, ExternalLinkIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { useAuth } from '../context/AuthContext';
import { AlphavantageDataService, AlphavantageMonthlyData, NewsItem, SearchResult, DividendData } from '../services/alphavantageData';
import { MarketQuote } from '../types/upstox';
import { CandleChart } from '../components/CandleChart';
import { motion } from 'framer-motion';
import debounce from 'lodash/debounce';

interface CandleData {
  timestamp: string;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
  oi: number;
}

// Updated QuoteCard with better UI
const QuoteCard: React.FC<{ quote: MarketQuote }> = ({ quote }) => {
  const bgColor = useColorModeValue('white', 'gray.800');
  const changeColor = quote.net_change >= 0 ? 'green.500' : 'red.500';
  const changePercentage = (quote.net_change / quote.ohlc.close) * 100;

  return (
    <Box
      p={{ base: 4, md: 6 }}
      bg={bgColor}
      boxShadow="xl"
      borderRadius="2xl"
      border="1px"
      borderColor={useColorModeValue('gray.200', 'gray.600')}
      mb={6}
      transition="all 0.2s"
      _hover={{ transform: 'translateY(-2px)', boxShadow: '2xl' }}
    >
      <VStack align="stretch" spacing={4}>
        <Heading size={{ base: "md", md: "lg" }} bgGradient="linear(to-r, blue.400, purple.500)" bgClip="text">
          {quote.symbol}
        </Heading>
        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={{ base: 4, md: 6 }}>
          <Stat>
            <StatLabel fontSize="sm">Last Price</StatLabel>
            <StatNumber fontSize="2xl" fontWeight="bold">
              ${quote.last_price.toFixed(2)}
            </StatNumber>
            <StatHelpText
              fontSize="md"
              color={quote.net_change >= 0 ? 'green.500' : 'red.500'}
            >
              <StatArrow type={quote.net_change >= 0 ? 'increase' : 'decrease'} />
              {Math.abs(changePercentage).toFixed(2)}%
            </StatHelpText>
          </Stat>
          <Stat>
            <StatLabel fontSize="sm">24h High</StatLabel>
            <StatNumber color="green.500">
              ${quote.ohlc.high.toFixed(2)}
            </StatNumber>
          </Stat>
          <Stat>
            <StatLabel fontSize="sm">24h Low</StatLabel>
            <StatNumber color="red.500">
              ${quote.ohlc.low.toFixed(2)}
            </StatNumber>
          </Stat>
          <Stat>
            <StatLabel fontSize="sm">Volume</StatLabel>
            <StatNumber>
              {(quote.volume / 1000000).toFixed(2)}M
            </StatNumber>
          </Stat>
        </SimpleGrid>
      </VStack>
    </Box>
  );
};

// Updated NewsCard with better UI
const NewsCard: React.FC<{ news: NewsItem }> = ({ news }) => {
  const bgColor = useColorModeValue('white', 'gray.800');
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Box
        p={6}
        bg={bgColor}
        boxShadow="lg"
        borderRadius="xl"
        border="1px"
        borderColor={useColorModeValue('gray.200', 'gray.600')}
        transition="all 0.2s"
        _hover={{ transform: 'translateY(-2px)', boxShadow: 'xl' }}
      >
        <Stack spacing={4}>
          <Heading as="h3" size="md">
            <Link href={news.url} isExternal>
              {news.title} <ExternalLinkIcon mx="2px" />
            </Link>
          </Heading>
          <Text fontSize="md" color="gray.600" noOfLines={3}>
            {news.description}
          </Text>
          <HStack justify="space-between" wrap="wrap">
            <Badge
              colorScheme="purple"
              px={3}
              py={1}
              borderRadius="full"
              fontSize="xs"
            >
              {new Date(news.publishedAt).toLocaleDateString(undefined, {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </Badge>
            <Text fontSize="sm" fontWeight="medium" color="gray.500">
              {news.source.name}
            </Text>
          </HStack>
        </Stack>
      </Box>
    </motion.div>
  );
};

// Updated SentimentCard to accept onTickerClick prop
const SentimentCard: React.FC<{ 
  news: NewsItem; 
  onTickerClick: (ticker: string) => void;
}> = ({ news, onTickerClick }) => {
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  
  const getSentimentColor = (label: string) => {
    switch (label) {
      case 'Bullish': return { bg: 'green.50', text: 'green.500', border: 'green.200' };
      case 'Somewhat-Bullish': return { bg: 'green.50', text: 'green.400', border: 'green.100' };
      case 'Neutral': return { bg: 'gray.50', text: 'gray.500', border: 'gray.200' };
      case 'Somewhat-Bearish': return { bg: 'red.50', text: 'red.400', border: 'red.100' };
      case 'Bearish': return { bg: 'red.50', text: 'red.500', border: 'red.200' };
      default: return { bg: 'gray.50', text: 'gray.500', border: 'gray.200' };
    }
  };

  const sentimentColors = getSentimentColor(news.overall_sentiment_label);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Box
        p={6}
        bg={bgColor}
        boxShadow="lg"
        borderRadius="xl"
        border="1px"
        borderColor={borderColor}
        position="relative"
        overflow="hidden"
        _hover={{ transform: 'translateY(-2px)', boxShadow: 'xl' }}
        transition="all 0.2s"
      >
        <Box
          position="absolute"
          top={0}
          right={0}
          px={4}
          py={2}
          bg={sentimentColors.bg}
          borderBottomLeftRadius="xl"
          borderTopRightRadius="xl"
          borderLeft="1px"
          borderBottom="1px"
          borderColor={sentimentColors.border}
        >
          <HStack spacing={2}>
            <Text fontSize="sm" fontWeight="bold" color={sentimentColors.text}>
              {news.overall_sentiment_label}
            </Text>
            <Badge variant="subtle" colorScheme={news.overall_sentiment_score >= 0 ? 'green' : 'red'}>
              {news.overall_sentiment_score.toFixed(2)}
            </Badge>
          </HStack>
        </Box>

        <VStack align="stretch" spacing={4}>
          <VStack align="stretch" spacing={2}>
            <Heading as="h3" size="md">
              <Link href={news.url} isExternal>
                {news.title} <ExternalLinkIcon mx="2px" />
              </Link>
            </Heading>
            <Text fontSize="sm" color="gray.500" noOfLines={3}>
              {news.summary}
            </Text>
          </VStack>

          <HStack justify="space-between" wrap="wrap">
            <Text fontSize="sm" color="gray.500">
              {new Date(news.time_published.replace(/(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})/, '$1-$2-$3T$4:$5:$6')).toLocaleString()}
            </Text>
            <Text fontSize="sm" color="gray.500">
              Source: {news.source}
            </Text>
          </HStack>

          {news.topics?.length > 0 && (
            <Wrap spacing={2}>
              {news.topics.map((topic, index) => (
                <WrapItem key={index}>
                  <Badge px={2} py={1} borderRadius="full" variant="subtle" colorScheme="purple" fontSize="xs">
                    {topic.topic}
                  </Badge>
                </WrapItem>
              ))}
            </Wrap>
          )}

          {news.ticker_sentiment?.length > 0 && (
            <Box>
              <Text fontSize="sm" fontWeight="medium" mb={2}>
                Related Tickers
              </Text>
              <Wrap spacing={2}>
                {news.ticker_sentiment.map((ticker, index) => (
                  <WrapItem key={index}>
                    <Badge
                      px={2}
                      py={1}
                      borderRadius="md"
                      variant="subtle"
                      colorScheme={parseFloat(ticker.ticker_sentiment_score) >= 0 ? 'green' : 'red'}
                      cursor="pointer"
                      onClick={() => onTickerClick(ticker.ticker)}
                      _hover={{
                        opacity: 0.8,
                        transform: 'scale(1.05)',
                      }}
                      transition="all 0.2s"
                    >
                      {ticker.ticker} ({parseFloat(ticker.ticker_sentiment_score).toFixed(2)})
                    </Badge>
                  </WrapItem>
                ))}
              </Wrap>
            </Box>
          )}
        </VStack>
      </Box>
    </motion.div>
  );
};

// Add new DividendCard component
const DividendCard: React.FC<{ data: DividendData[] }> = ({ data }) => {
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  const nextDividend = data.find(d => new Date(d.payment_date) > new Date());
  const pastDividends = data
    .filter(d => new Date(d.payment_date) <= new Date())
    .slice(0, 3); // Show last 3 dividends

  const formatDate = (date: string) => new Date(date).toLocaleDateString();
  const formatAmount = (amount: string) => 
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })
      .format(parseFloat(amount));

  return (
    <Box
      p={6}
      bg={bgColor}
      boxShadow="lg"
      borderRadius="xl"
      border="1px"
      borderColor={borderColor}
    >
      <VStack align="stretch" spacing={4}>
        <Heading size="md">Dividend Information</Heading>
        
        {nextDividend && (
          <Box>
            <Text fontWeight="bold" color="green.500" mb={2}>
              Next Dividend
            </Text>
            <SimpleGrid columns={2} spacing={4} p={4} bg="green.50" borderRadius="md">
              <Box>
                <Text fontSize="sm" color="gray.600">Amount</Text>
                <Text fontWeight="bold" color="green.600">
                  {formatAmount(nextDividend.amount)}
                </Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.600">Payment Date</Text>
                <Text fontWeight="bold" color="green.600">
                  {formatDate(nextDividend.payment_date)}
                </Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.600">Ex-Dividend Date</Text>
                <Text fontWeight="bold" color="green.600">
                  {formatDate(nextDividend.ex_dividend_date)}
                </Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.600">Record Date</Text>
                <Text fontWeight="bold" color="green.600">
                  {formatDate(nextDividend.record_date)}
                </Text>
              </Box>
            </SimpleGrid>
          </Box>
        )}

        <Box>
          <Text fontWeight="bold" mb={2}>Past Dividends</Text>
          <Table size="sm" variant="simple">
            <Thead>
              <Tr>
                <Th>Date</Th>
                <Th>Amount</Th>
                <Th>Type</Th>
              </Tr>
            </Thead>
            <Tbody>
              {pastDividends.map((div, idx) => (
                <Tr key={idx}>
                  <Td>{formatDate(div.payment_date)}</Td>
                  <Td>{formatAmount(div.amount)}</Td>
                  <Td>
                    <Badge colorScheme="purple">Regular</Badge>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      </VStack>
    </Box>
  );
};

// Updated search section with better UI
const SearchSection = ({
  search,
  isSearching,
  searchResults,
  handleSearchChange,
  handleKeyDown,
  handleResultSelect
}) => {
  const bgColor = useColorModeValue('white', 'gray.800');
  
  return (
    <Box w="full" maxW={{ base: "100%", md: "800px" }} mx="auto" mb={12}>
      <Popover
        isOpen={searchResults.length > 0}
        autoFocus={false}
        matchWidth
        placement="bottom"
      >
        <PopoverTrigger>
          <Box position="relative">
            <InputGroup size="lg">
              <InputLeftElement pointerEvents="none" h="full" pl={4}>
                {isSearching ? (
                  <Spinner size="sm" color="blue.500" />
                ) : (
                  <SearchIcon color="blue.500" boxSize={5} />
                )}
              </InputLeftElement>
              <Input
                placeholder="Search stocks (e.g., AAPL, MSFT, GOOGL)"
                size={{ base: "md", md: "lg" }}
                height={{ base: "50px", md: "70px" }}
                fontSize={{ base: "md", md: "xl" }}
                pl={14}
                borderRadius="2xl"
                bg={bgColor}
                boxShadow="2xl"
                borderWidth={2}
                borderColor="blue.100"
                _hover={{ borderColor: 'blue.200' }}
                _focus={{ borderColor: 'blue.300', boxShadow: 'outline' }}
                value={search}
                onChange={handleSearchChange}
                onKeyDown={handleKeyDown}
              />
            </InputGroup>
          </Box>
        </PopoverTrigger>
        <PopoverContent 
          borderRadius="xl" 
          boxShadow="xl" 
          border="1px"
          borderColor={useColorModeValue('gray.200', 'gray.600')}
          maxH="400px"
          overflowY="auto"
        >
          <PopoverBody p={0}>
            <List spacing={0}>
              {searchResults.map((result) => (
                <ListItem
                  key={result.symbol}
                  p={4}
                  cursor="pointer"
                  transition="all 0.2s"
                  _hover={{ bg: useColorModeValue('blue.50', 'blue.900') }}
                  onClick={() => handleResultSelect(result.symbol)}
                >
                  <HStack spacing={4}>
                    <Box
                      bg={useColorModeValue('blue.100', 'blue.700')}
                      p={2}
                      borderRadius="lg"
                    >
                      <Text fontWeight="bold" fontSize="lg">
                        {result.symbol}
                      </Text>
                    </Box>
                    <VStack align="start" spacing={0}>
                      <Text fontWeight="medium">{result.name}</Text>
                      <HStack spacing={2}>
                        <Badge colorScheme="purple">
                          {result.type}
                        </Badge>
                        <Badge colorScheme="blue">
                          {result.region}
                        </Badge>
                        <Badge colorScheme="green">
                          {result.currency}
                        </Badge>
                      </HStack>
                    </VStack>
                  </HStack>
                </ListItem>
              ))}
            </List>
          </PopoverBody>
        </PopoverContent>
      </Popover>
    </Box>
  );
};

export const MarketSearch: React.FC = () => {
  // 1. Move all hooks to the top level and keep them in consistent order
  const { upstoxToken } = useAuth();
  const toast = useToast();
  const bgColor = useColorModeValue('white', 'gray.800');
  
  // Group all useState hooks together
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [quote, setQuote] = useState<MarketQuote | null>(null);
  const [candleData, setCandleData] = useState<CandleData[]>([]);
  // Remove candleInterval state since we're only using monthly data for Alpha Vantage
  const [news, setNews] = useState<NewsItem[]>([]);
  const [sentimentNews, setSentimentNews] = useState<NewsItem[]>([]);
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [dividendData, setDividendData] = useState<DividendData[]>([]);

  // Create debounced search function
  const debouncedSearch = React.useCallback(
    debounce(async (query: string) => {
      if (!query) {
        setSearchResults([]);
        return;
      }

      try {
        setIsSearching(true);
        const alphavantage = new AlphavantageDataService();
        const results = await alphavantage.searchSymbol(query);
        setSearchResults(results);
      } catch (error) {
        console.error('Symbol search failed:', error);
      } finally {
        setIsSearching(false);
      }
    }, 300),
    []
  );

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearch(value);
    debouncedSearch(value);
  };

  // Handle search result selection
  const handleResultSelect = (symbol: string) => {
    setSearch(symbol);
    setSearchResults([]); // Clear results
    handleSearch(symbol);
  };

  const handleSearch = async (query: string) => {
    if (!query) return;
    
    setIsLoading(true);
    setError(null);
    const symbol = query.toUpperCase();

    try {
      const alphavantage = new AlphavantageDataService();
      
      // Fetch data in parallel
      const [monthlyData, newsResponse, sentimentResponse, dividendResponse] = await Promise.all([
        alphavantage.getMonthlyAdjusted(symbol)
          .catch(err => {
            console.error('Market data fetch failed:', err);
            return [];
          }),
        
        fetch(`https://finomo-middleware.ml-meastro.workers.dev/api/news/search/${symbol}?locale=en-US`)
          .then(res => res.json())
          .catch(err => {
            console.error('News fetch failed:', err);
            return { success: false, data: { items: [] } };
          }),
        alphavantage.getNewsSentiment(symbol)
          .catch(err => {
            console.error('Sentiment fetch failed:', err);
            return { feed: [] };
          }),
        alphavantage.getDividends(symbol)
          .catch(err => {
            console.error('Dividend fetch failed:', err);
            return { data: [] };
          })
      ]);

      // Format news items from the response with sorting
      const formattedNews = (newsResponse.data?.items || []).map((item: any) => {
        // Extract source from title
        const titleParts = item.title?.split(' - ');
        const mainTitle = titleParts?.[0] || '';
        const sourceName = titleParts?.[titleParts.length - 1] || 'Unknown Source';

        // Clean up description
        const cleanDescription = item.description
          ?.replace(/&lt;/g, '<')
          .replace(/&gt;/g, '>')
          .replace(/&nbsp;/g, ' ')
          .replace(/<[^>]*>/g, '')
          .replace(/^[^>]*>[^<]*<\/a>\s*/, '')
          .trim();

        return {
          title: mainTitle,
          description: cleanDescription || '',
          url: item.link || '',
          publishedAt: item.pubDate || new Date().toISOString(),
          source: {
            name: sourceName
          }
        };
      })
      // Sort news by publishedAt in descending order (latest first)
      .sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime());

      // Create quote-like object from latest monthly data
      const latestData = monthlyData[monthlyData.length - 1];
      const previousData = monthlyData[monthlyData.length - 2];
      
      if (latestData) {
        const netChange = latestData.close - previousData.close;
        const quote = {
          symbol,
          last_price: latestData.close,
          net_change: netChange,
          volume: latestData.volume,
          ohlc: {
            open: latestData.open,
            high: latestData.high,
            low: latestData.low,
            close: latestData.close
          }
        };
        
        setQuote(quote);
        setCandleData(monthlyData.map(d => ({
          timestamp: d.timestamp,
          open: d.open,
          high: d.high,
          low: d.low,
          close: d.close,
          volume: d.volume,
          oi: 0
        })));
      }

      setNews(formattedNews);
      // Sort sentiment news by time_published in descending order (latest first)
      const sortedSentimentNews = (sentimentResponse.feed || []).sort((a, b) => {
        const timeA = new Date(a.time_published.replace(/(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})/, '$1-$2-$3T$4:$5:$6')).getTime();
        const timeB = new Date(b.time_published.replace(/(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})/, '$1-$2-$3T$4:$5:$6')).getTime();
        return timeB - timeA;
      });

      setSentimentNews(sortedSentimentNews);
      setDividendData(dividendResponse.data || []);
      console.log('Formatted News:', formattedNews); // Debug log

      // Show error if no data found
      if (!latestData && formattedNews.length === 0) {
        setError('No data found for this symbol');
      }

    } catch (error) {
      console.error('Search failed:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch data. Please try again.',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch(search);
    }
  };

  // Add handler for ticker clicks
  const handleTickerClick = (ticker: string) => {
    setSearch(ticker);
    handleSearch(ticker);
  };

  return (
    <Container maxW="container.xl" py={{ base: 4, md: 12 }}>
      <VStack spacing={{ base: 4, md: 8 }} align="stretch">
        <Text
          fontSize={{ base: "3xl", md: "5xl" }}
          fontWeight="bold"
          bgGradient="linear(to-r, blue.400, purple.500)"
          bgClip="text"
          textAlign="center"
          mb={8}
        >
          Market Search
        </Text>

        <SearchSection
          search={search}
          isSearching={isSearching}
          searchResults={searchResults}
          handleSearchChange={handleSearchChange}
          handleKeyDown={handleKeyDown}
          handleResultSelect={handleResultSelect}
        />

        {isLoading && (
          <Box textAlign="center">
            <Spinner size="xl" />
          </Box>
        )}

        {quote || candleData.length > 0 || news.length > 0 ? (
          <Grid templateColumns={{ base: "1fr", lg: "3fr 2fr" }} gap={{ base: 4, md: 6 }}>
            <GridItem>
              {quote && <QuoteCard quote={quote} />}
              {dividendData.length > 0 && <DividendCard data={dividendData} />}
              {candleData.length > 0 && (
                <Box
                  bg={useColorModeValue('white', 'gray.800')}
                  p={6}
                  borderRadius="lg"
                  boxShadow="xl"
                  mb={6}
                >
                  <CandleChart
                    data={candleData}
                    isLoading={isLoading}
                    selectedInterval="month"  // Fixed interval for Alpha Vantage
                    onIntervalChange={() => {}} // Empty function since we don't support interval changes
                  />
                </Box>
              )}
              {/* Move news section here */}
              <VStack spacing={4} align="stretch">
                <Heading size="md">Latest News</Heading>
                {news.map((item, index) => (
                  <NewsCard key={index} news={item} />
                ))}
                {news.length === 0 && (
                  <Text color="gray.500" textAlign="center">
                    No news available
                  </Text>
                )}
              </VStack>
            </GridItem>

            {/* Update sentiment section with onTickerClick */}
            <GridItem>
              <VStack spacing={4} align="stretch">
                <Heading size="md">Market Sentiment</Heading>
                {sentimentNews.length > 0 ? (
                  sentimentNews.map((item, index) => (
                    <SentimentCard 
                      key={index} 
                      news={item} 
                      onTickerClick={handleTickerClick}
                    />
                  ))
                ) : (
                  <Text color="gray.500" textAlign="center">
                    No sentiment data available
                  </Text>
                )}
              </VStack>
            </GridItem>
          </Grid>
        ) : error ? (
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            {error}
          </Alert>
        ) : null}
      </VStack>
    </Container>
  );
};
